import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Loader2, CheckCircle, XCircle } from 'lucide-react';

const OAuthCallback = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [status, setStatus] = useState('processing'); // 'processing', 'success', 'error'
  const [message, setMessage] = useState('Completing sign-in...');

  useEffect(() => {
    const handleOAuthCallback = () => {
      const token = searchParams.get('token');
      const userId = searchParams.get('user_id');
      const email = searchParams.get('email');
      const role = searchParams.get('role');
      const tier = searchParams.get('tier');
      const orgId = searchParams.get('org_id');
      const orgName = searchParams.get('org_name');
      const error = searchParams.get('error');

      if (error) {
        setStatus('error');
        setMessage('Authentication failed. Please try again.');
        setTimeout(() => navigate('/'), 3000);
        return;
      }

      if (token && userId && email) {
        // Save auth data to localStorage
        localStorage.setItem('token', token);
        localStorage.setItem('userId', userId);
        localStorage.setItem('userEmail', email);

        if (role) localStorage.setItem('userRole', role);
        if (tier) localStorage.setItem('userTier', tier);
        if (orgId) localStorage.setItem('userOrgId', orgId);
        if (orgName) localStorage.setItem('userOrgName', orgName);

        setStatus('success');
        setMessage('Sign-in successful! Redirecting...');

        // Redirect to main app
        setTimeout(() => {
          window.location.href = '/';
        }, 1500);
      } else {
        setStatus('error');
        setMessage('Invalid authentication response. Please try again.');
        setTimeout(() => navigate('/'), 3000);
      }
    };

    handleOAuthCallback();
  }, [searchParams, navigate]);

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-blue-50 to-indigo-100 dark:from-gray-900 dark:to-gray-800">
      <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-2xl p-8 max-w-md w-full mx-4">
        <div className="flex flex-col items-center space-y-6">
          {/* Icon */}
          <div className="relative">
            {status === 'processing' && (
              <div className="w-20 h-20 bg-blue-100 dark:bg-blue-900 rounded-full flex items-center justify-center">
                <Loader2 className="w-10 h-10 text-blue-600 dark:text-blue-400 animate-spin" />
              </div>
            )}
            {status === 'success' && (
              <div className="w-20 h-20 bg-green-100 dark:bg-green-900 rounded-full flex items-center justify-center animate-in zoom-in duration-300">
                <CheckCircle className="w-10 h-10 text-green-600 dark:text-green-400" />
              </div>
            )}
            {status === 'error' && (
              <div className="w-20 h-20 bg-red-100 dark:bg-red-900 rounded-full flex items-center justify-center animate-in zoom-in duration-300">
                <XCircle className="w-10 h-10 text-red-600 dark:text-red-400" />
              </div>
            )}
          </div>

          {/* Title */}
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white text-center">
            {status === 'processing' && 'Signing you in...'}
            {status === 'success' && 'Welcome to VerbaGPT!'}
            {status === 'error' && 'Oops!'}
          </h1>

          {/* Message */}
          <p className="text-gray-600 dark:text-gray-400 text-center">
            {message}
          </p>

          {/* Progress indicator for processing state */}
          {status === 'processing' && (
            <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2 overflow-hidden">
              <div className="h-full bg-gradient-to-r from-blue-500 to-indigo-600 animate-pulse" style={{ width: '70%' }}></div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default OAuthCallback;
